import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Send, Upload, Bot, FileText, Loader2, Sparkles, User,
  BarChart3, Calculator, Shield, X, CheckCircle, XCircle,
  AlertTriangle, Download, Save, ChevronDown, DollarSign, TrendingUp
} from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import { toast } from 'sonner';
import SBAEligibilityCard from './SBAEligibilityCard';
import { getForeclosureName } from '../applications/loanFieldConstants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ---- Credit Analyzer Logic ----
const calculateCreditScore = (params) => {
  let totalScore = 0;
  const positives = [];
  const negatives = [];

  let s1 = 0;
  if (parseFloat(params.annualRevenue) >= 100000) { s1 += 6; positives.push('Strong annual revenue (>$100K)'); }
  else if (parseFloat(params.annualRevenue) >= 50000) { s1 += 3; positives.push('Moderate annual revenue ($50K-$100K)'); }
  else { negatives.push('Low annual revenue (<$50K)'); }
  const dsr = parseFloat(params.annualDebtService) / parseFloat(params.annualRevenue);
  if (dsr < 0.25) { s1 += 6; positives.push('Excellent debt service ratio (<25%)'); }
  else if (dsr < 0.4) { s1 += 3; positives.push('Good debt service ratio (25-40%)'); }
  else { negatives.push('High debt service ratio (>40%)'); }
  if (parseFloat(params.ebitda) > 0) { s1 += 5; positives.push('Positive EBITDA'); }
  else { negatives.push('Negative or zero EBITDA'); }

  let s2 = 0;
  if (params.positiveBalance === 'yes') { s2 += 6; positives.push('Maintains positive bank balance'); } else { negatives.push('Does not maintain positive bank balance'); }
  if (params.negativeBalances === 'no') { s2 += 6; positives.push('No negative balances in past 6 months'); } else { negatives.push('Had negative balances in past 6 months'); }
  if (params.consistentDeposits === 'yes') { s2 += 5; positives.push('Consistent deposit history (3+ months)'); } else { negatives.push('Inconsistent deposit history'); }

  let s3 = 0;
  if (params.ownsHome === 'yes') { s3 += 4; positives.push('Owns home (asset strength)'); }
  if (params.hasGuarantor === 'yes') { s3 += 4; positives.push('Has guarantor'); }
  if (params.hasWebsite === 'yes') { s3 += 4; positives.push('Has functioning business website'); } else { negatives.push('No functioning business website'); }
  if (params.newCustomer === 'yes') { s3 += 5; positives.push('New customer to Quidity'); }

  let s4 = 0;
  if (params.hasDnB === 'yes') { s4 += 6; positives.push('Has D&B profile'); } else { negatives.push('No D&B profile'); }
  if (params.pastDueAccounts === 'no') { s4 += 6; positives.push('No past-due accounts (12 months)'); } else { negatives.push('Has past-due accounts in last 12 months'); }
  if (params.tradeCredit === 'yes') { s4 += 5; positives.push('Good trade credit history'); } else { negatives.push('Poor or no trade credit history'); }

  let s5 = 0;
  if (params.newInquiries === 'no') { s5 += 9; positives.push('Limited new inquiries'); } else { negatives.push('Too many new inquiries'); }
  if (params.businessCreditScore === 'yes') { s5 += 8; positives.push('Strong business credit score (>70)'); } else { negatives.push('Weak business credit score'); }

  let s6 = 0;
  if (parseFloat(params.ficoScore) >= 675 || params.ficoScore === 'yes') { s6 += 6; positives.push('Good personal FICO score (>=675)'); } else { negatives.push('Poor personal FICO score (<675)'); }
  if (params.hasCollections === 'no') { s6 += 6; positives.push('No recent collections'); } else { negatives.push('Has collections in last 12 months'); }
  if (params.hasInstallmentTrades === 'yes') { s6 += 5; positives.push('Active positive trade history'); } else { negatives.push('No positive trade history'); }

  const max = 17;
  totalScore = Math.round(([s1, s2, s3, s4, s5, s6].reduce((a, v) => a + (v / max) * 100 * (1 / 6), 0)));
  let riskLevel = 'high', riskColor = 'bg-red-500';
  if (totalScore >= 70) { riskLevel = 'low'; riskColor = 'bg-emerald-500'; }
  else if (totalScore >= 50) { riskLevel = 'medium'; riskColor = 'bg-amber-500'; }
  return { totalScore, riskLevel, riskColor, positives, negatives };
};

// ---- Amortization Logic ----
const calculateAmortization = (params) => {
  const { loan_type, amount, rate, term_years, prime_rate, loc_term_months } = params;
  const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
  if (loan_type === 'line_of_credit') {
    const r = 0.19; const months = loc_term_months || 6;
    const interest = amount * r * (months / 12); const total = amount + interest;
    return { monthlyPayment: fmt(total / months), totalInterest: fmt(interest), totalRepayment: fmt(total), detail: `${months} months at 19% annual` };
  }
  if (loan_type === 'sba_loan') {
    const r = ((prime_rate || 6.75) + 2.75) / 100; const mr = r / 12; const n = 120;
    const mp = amount * (mr * Math.pow(1 + mr, n)) / (Math.pow(1 + mr, n) - 1);
    return { monthlyPayment: fmt(mp), totalInterest: fmt(mp * n - amount), totalRepayment: fmt(mp * n), detail: `${(r * 100).toFixed(2)}% (Prime+2.75%) over 10 years` };
  }
  if (loan_type === 'jumbo_term_loan') {
    // Simple interest at 24% annualized over 24 months
    const months = 24;
    const interest = amount * 0.24 * (months / 12);
    const total = amount + interest;
    return {
      monthlyPayment: fmt(total / months),
      totalInterest: fmt(interest),
      totalRepayment: fmt(total),
      detail: '24% simple interest over 24 months (Quidity Jumbo Term Loan)',
    };
  }
  if (loan_type === 'jumbo_rbf') {
    // Simple interest at 24% annualized over 12 months
    const months = 12;
    const interest = amount * 0.24 * (months / 12);
    const total = amount + interest;
    return {
      monthlyPayment: fmt(total / months),
      totalInterest: fmt(interest),
      totalRepayment: fmt(total),
      detail: '24% simple interest over 12 months (Quidity Jumbo RBF)',
    };
  }
  const r = (rate || 16) / 100; const mr = r / 12; const n = (term_years || 10) * 12;
  const mp = amount * (mr * Math.pow(1 + mr, n)) / (Math.pow(1 + mr, n) - 1);
  return { monthlyPayment: fmt(mp), totalInterest: fmt(mp * n - amount), totalRepayment: fmt(mp * n), detail: `${rate || 16}% over ${term_years || 10} years` };
};

// ---- PDF Export ----
const generatePDF = (title, sections) => {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  let y = 20;
  const margin = 20;
  const lineH = 7;
  const checkPage = (needed) => { if (y + needed > doc.internal.pageSize.getHeight() - 20) { doc.addPage(); y = 20; } };

  doc.setFontSize(10); doc.setTextColor(100);
  doc.text('Quidity Direct', margin, y);
  doc.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), pageW - margin, y, { align: 'right' });
  y += 12;
  doc.setFontSize(18); doc.setTextColor(30); doc.text(title, margin, y); y += 12;
  doc.setDrawColor(200); doc.line(margin, y, pageW - margin, y); y += 10;

  sections.forEach(section => {
    checkPage(30);
    if (section.heading) { doc.setFontSize(12); doc.setTextColor(60); doc.text(section.heading, margin, y); y += 8; }
    if (section.rows) {
      doc.setFontSize(10);
      section.rows.forEach(row => { checkPage(lineH); doc.setTextColor(100); doc.text(row.label, margin + 2, y); doc.setTextColor(30); doc.text(String(row.value), pageW - margin, y, { align: 'right' }); y += lineH; });
      y += 4;
    }
    if (section.items) {
      doc.setFontSize(9);
      section.items.forEach(item => {
        checkPage(lineH);
        const prefix = item.type === 'positive' || item.type === 'met' ? '  [MET] ' : item.type === 'cannot_verify' ? '  [?] ' : '  [X] ';
        const r = item.type === 'positive' || item.type === 'met' ? 34 : item.type === 'cannot_verify' ? 150 : 180;
        const g = item.type === 'positive' || item.type === 'met' ? 139 : item.type === 'cannot_verify' ? 120 : 60;
        doc.setTextColor(r, g, item.type === 'positive' || item.type === 'met' ? 34 : 60);
        const lines = doc.splitTextToSize(prefix + item.text, pageW - margin * 2 - 4);
        lines.forEach(line => { checkPage(lineH); doc.text(line, margin + 2, y); y += lineH; });
      });
      y += 4;
    }
  });
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i); doc.setFontSize(8); doc.setTextColor(150);
    doc.text(`Page ${i} of ${totalPages}`, pageW / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
  }
  doc.save(`${title.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}.pdf`);
};


const INITIAL_MESSAGE = { role: 'assistant', content: "Hi! I'm your Quidity toolkit assistant. I can help you with:\n\n**Pre-Underwriting** — Upload documents or tell me about a business to evaluate eligibility against any of our programs: Line of Credit, Growth Term Loan, Business Term Loan (3-5yr), **Quidity Jumbo Revenue Based Financing (RBF)**, and **Quidity Jumbo Term Loan (TL)**.\n\n**Credit Analysis** — Evaluate business creditworthiness across 6 scoring categories\n\n**Loan Calculator** — Calculate payments for Term Loans, SBA Loans, Lines of Credit, the **Quidity Jumbo Term Loan** (24% simple interest / 24 months), and the **Quidity Jumbo RBF** (24% simple interest / 12 months)\n\nYou can also ask me questions about our underwriting requirements. What would you like to do?" };

const LOC_QUALIFICATION_PROMPT = `Based on the bank statements I've uploaded, evaluate this business against our Line of Credit underwriting requirements. Check each criterion:

1. **Minimum Daily Cash Balance of $10,000** — Does the average daily balance meet or exceed $10,000 across the statements?
2. **Turnover of $30,000/month and Growing** — Are total monthly deposits at least $30,000? Is the trend steady or growing?
3. **No MCA Loans in Past 2 Months** — Are there any signs of MCA (Merchant Cash Advance) debits or daily/weekly fixed withdrawals consistent with MCA repayments? Flag any MCA activity or cash flow distress.
4. **No Adverse Credit** — Are there any NSF fees, overdrafts, returned checks, or negative balance days that indicate adverse credit behavior?
5. **Cash Flow Health** — Is the business cash-flow positive? What is the net monthly income/loss trend?

For each criterion, state:
- MET / NOT MET / CANNOT VERIFY
- The specific data point from the statements that supports the determination

Then provide a final **LOC QUALIFICATION SUMMARY**:
- Overall recommendation: LIKELY QUALIFIES / DOES NOT QUALIFY / NEEDS FURTHER REVIEW
- Recommended LOC amount (based on revenue)
- Risk level: Low / Medium / High
- Any conditions or additional documents needed

NOTE: Also remind the reviewer to verify separately:
- 2 years in business (not determinable from bank statements alone)
- Equifax 675+, FICO 5 (requires credit pull)
- State restrictions: Nevada, North Dakota, South Dakota, Tennessee, Puerto Rico, Hawaii, and California`;


const BrokerToolkit = () => {
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [sessionId, setSessionId] = useState(() => `toolkit_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const [activeResult, setActiveResult] = useState(null);
  const [creditParams, setCreditParams] = useState({});
  const [amortParams, setAmortParams] = useState({});
  const [uwParams, setUwParams] = useState({});
  const [savingToApp, setSavingToApp] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [applications, setApplications] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState('');

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('auth_token');

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchApplications = async () => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/applications`, { headers: { Authorization: `Bearer ${token}` } });
      setApplications(resp.data || []);
    } catch (err) { console.error('Error fetching applications', err); }
  };

  const sendMessage = useCallback(async (text) => {
    if (!text?.trim() || loading) return;
    const userMsg = text.trim();
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setInput('');
    setLoading(true);

    try {
      const resp = await axios.post(`${BACKEND_URL}/api/toolkit/ai-chat`, {
        message: userMsg,
        conversation: messages,
        session_id: sessionId,
        active_tool: activeResult?.type || null,
        current_params: activeResult?.type === 'credit_analyzer' ? creditParams : activeResult?.type === 'amortization_calculator' ? amortParams : uwParams
      }, { headers: { Authorization: `Bearer ${token}` } });

      const { message: aiMsg, tool_action } = resp.data;
      setMessages(prev => [...prev, { role: 'assistant', content: aiMsg }]);
      if (tool_action) handleToolAction(tool_action);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong. Please try again.' }]);
    } finally { setLoading(false); }
  }, [loading, messages, token, activeResult, creditParams, amortParams, uwParams]);

  const handleToolAction = (action) => {
    const { tool, parameters } = action;
    if (tool === 'credit_analyzer' && parameters) {
      const merged = { ...creditParams, ...parameters };
      setCreditParams(merged);
      setActiveResult({ type: 'credit_analyzer', data: calculateCreditScore(merged), params: merged });
    }
    if (tool === 'amortization_calculator' && parameters) {
      const merged = { ...amortParams, ...parameters };
      setAmortParams(merged);
      setActiveResult({ type: 'amortization_calculator', data: calculateAmortization(merged), params: merged });
    }
    if (tool === 'pre_underwriting' && parameters) {
      setUwParams(parameters);
      setActiveResult({ type: 'pre_underwriting', data: parameters });
    }
  };

  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    // Process each file sequentially to maintain conversation context
    for (const file of files) {
      await processFile(file);
    }
    // Reset input so same files can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const processFile = async (file) => {
    setUploadingDoc(true);
    setMessages(prev => [...prev, { role: 'user', content: `[Uploaded: ${file.name}]` }]);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tool_context', activeResult?.type || 'general');
      fd.append('session_id', sessionId);
      fd.append('conversation', JSON.stringify(messages.slice(-20)));
      const resp = await axios.post(`${BACKEND_URL}/api/toolkit/ai-extract`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { message: aiMsg, extracted_data, recommended_tool } = resp.data;
      setMessages(prev => [...prev, { role: 'assistant', content: aiMsg }]);

      // Check for tax return analysis data
      const taxData = extracted_data?.tax_return_analysis;
      const hasTaxData = taxData && Object.values(taxData).some(v => v !== null && v !== undefined);

      // Check for bank statement analysis data
      const bankData = extracted_data?.bank_statement_analysis;
      const hasBankData = bankData && (bankData.months?.length > 0 || bankData.average_balance !== null);

      if (hasTaxData) {
        // Merge with existing tax return analysis (e.g., debt schedule uploaded after tax return)
        setActiveResult(prev => {
          if (prev?.type === 'tax_return_analysis' && prev.data) {
            const merged = { ...prev.data };
            for (const [key, val] of Object.entries(taxData)) {
              if (val !== null && val !== undefined) {
                merged[key] = val;
              }
            }
            return { type: 'tax_return_analysis', data: merged };
          }
          return { type: 'tax_return_analysis', data: taxData };
        });
      } else if (hasBankData) {
        // Merge with existing bank statement data if present
        setActiveResult(prev => {
          if (prev?.type === 'bank_statement_analysis' && prev.data?.months) {
            const existingMonths = prev.data.months || [];
            const newMonths = bankData.months || [];
            // Merge months, avoid duplicates by month_year key
            const merged = [...existingMonths];
            for (const nm of newMonths) {
              if (!merged.find(m => m.month_year === nm.month_year)) {
                merged.push(nm);
              }
            }
            merged.sort((a, b) => (a.month_year || '').localeCompare(b.month_year || ''));
            const allBalances = merged.map(m => m.average_daily_balance).filter(v => v != null);
            const totalNsf = merged.reduce((s, m) => s + (m.nsf_count || 0), 0);
            const totalDeposits = merged.reduce((s, m) => s + (m.total_deposits || 0), 0);
            const totalWithdrawals = merged.reduce((s, m) => s + (m.total_withdrawals || 0), 0);
            return {
              type: 'bank_statement_analysis',
              data: {
                ...bankData,
                months: merged,
                average_balance: allBalances.length > 0 ? allBalances.reduce((a, b) => a + b, 0) / allBalances.length : null,
                total_nsf: totalNsf,
                total_deposits: totalDeposits,
                total_withdrawals: totalWithdrawals,
              }
            };
          }
          return { type: 'bank_statement_analysis', data: bankData };
        });
      } else {
        if (extracted_data?.credit_analyzer) {
          const clean = Object.fromEntries(Object.entries(extracted_data.credit_analyzer).filter(([, v]) => v !== null));
          if (Object.keys(clean).length > 0) setCreditParams(prev => ({ ...prev, ...clean }));
        }
        if (extracted_data?.amortization) {
          const clean = Object.fromEntries(Object.entries(extracted_data.amortization).filter(([, v]) => v !== null));
          if (Object.keys(clean).length > 0) setAmortParams(prev => ({ ...prev, ...clean }));
        }
        if (extracted_data?.pre_underwriting) {
          const clean = Object.fromEntries(Object.entries(extracted_data.pre_underwriting).filter(([, v]) => v !== null));
          if (Object.keys(clean).length > 0) {
            setUwParams(prev => ({ ...prev, ...clean }));
            setTimeout(() => {
              sendMessage(`I just uploaded "${file.name}". Based on the extracted data, please evaluate eligibility against our underwriting guidelines.`);
            }, 500);
          }
        }
      }

      setUploadingDoc(false);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.message || err?.response?.data?.detail;
      const errCode = err?.code || '';
      const netHint = (errCode === 'ECONNABORTED') ? ' (request timed out)'
        : (errCode === 'ERR_NETWORK') ? ' (network error — gateway / CORS / DNS)'
        : '';
      let errorMsg;
      if (detail) {
        errorMsg = detail;
      } else if (status === 500) {
        errorMsg = `The server had trouble processing "${file.name}". For scanned PDFs, try taking a photo/screenshot of the tax return pages and uploading the image instead — this gives much better results.`;
      } else {
        errorMsg = `I had trouble reading "${file.name}". Try uploading a clear image (PNG/JPG) of the document instead of a PDF — image uploads are more reliable for scanned documents.`;
      }
      const diag = `[diagnostic — HTTP ${status || 'no-response'}${netHint}${err?.message ? ` — ${err.message}` : ''}]`;
      setMessages(prev => [...prev, { role: 'assistant', content: `${errorMsg}\n\n${diag}` }]);
      setUploadingDoc(false);
    } finally { if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleSaveToApp = async () => {
    if (!selectedAppId || !activeResult) return;
    setSavingToApp(true);
    try {
      await axios.post(`${BACKEND_URL}/api/toolkit/save-to-application`, {
        application_id: selectedAppId,
        tool_type: activeResult.type,
        result_data: activeResult.data
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 });
      setShowSaveModal(false);
      toast.success('Saved to application — open the Review screen and check Analysis & Reports.', { duration: 6000 });
      setMessages(prev => [...prev, { role: 'assistant', content: `✓ Results saved to application successfully. Open the Review screen on this application to see them under "Analysis & Reports".` }]);
    } catch (err) {
      // Recovery path — the backend may have actually saved the result while
      // the response was lost (proxy / mobile network drop).  Quickly re-fetch
      // the application and see if a toolkit_result of this type just got added.
      let saved = false;
      try {
        const beforeAt = Date.now() - 5000;
        await new Promise((r) => setTimeout(r, 1500));
        const { data: fresh } = await axios.get(`${BACKEND_URL}/api/applications/${selectedAppId}`, { headers: { Authorization: `Bearer ${token}` } });
        const matches = (fresh?.toolkit_results || []).find((r) => {
          if (r?.tool_type !== activeResult.type) return false;
          const savedAt = r?.saved_at ? Date.parse(r.saved_at) : 0;
          return savedAt >= beforeAt;
        });
        if (matches) saved = true;
      } catch (_e) { /* ignore */ }
      if (saved) {
        setShowSaveModal(false);
        toast.success('Saved to application (request took longer than usual but completed).', { duration: 6000 });
        setMessages(prev => [...prev, { role: 'assistant', content: `✓ Results saved to application successfully. Open the Review screen to see them under "Analysis & Reports".` }]);
      } else {
        const status = err?.response?.status;
        const diag = `[diagnostic — HTTP ${status || 'no-response'}${err?.message ? ` — ${err.message}` : ''}]`;
        toast.error('Save to application failed — please try again.', { duration: 6000 });
        setMessages(prev => [...prev, { role: 'assistant', content: `Failed to save results. Please try again.\n\n${diag}` }]);
      }
    } finally { setSavingToApp(false); }
  };

  const openSaveModal = () => {
    fetchApplications();
    setShowSaveModal(true);
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } };

  const clearConversation = () => {
    setMessages([INITIAL_MESSAGE]);
    setSessionId(`toolkit_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
    setActiveResult(null);
    setCreditParams({});
    setAmortParams({});
    setUwParams({});
  };

  const quickActions = [
    { label: 'Pre-underwriting check', icon: Shield, msg: 'I want to check if a business qualifies for a loan. What information do you need?' },
    { label: 'Calculate loan payments', icon: Calculator, msg: 'Help me calculate loan payments. What type of loan should we look at?' },
    { label: 'Run credit analysis', icon: BarChart3, msg: 'I want to run a business credit analysis. What information do you need?' },
  ];

  return (
    <div className="flex gap-0 h-[calc(100vh-220px)] min-h-[600px]" data-testid="broker-toolkit">
      {/* Left: AI Chat */}
      <div className="flex-1 flex flex-col border border-slate-200 rounded-l-xl bg-white overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3.5 border-b bg-[#081120] text-white">
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center"><Sparkles className="h-4 w-4" /></div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold">Toolkit Assistant</h3>
            <p className="text-xs text-slate-300">Pre-underwriting, credit analysis, loan calculations</p>
          </div>
          {messages.length > 1 && (
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-white/10 h-7 text-xs" onClick={clearConversation} data-testid="clear-conversation-btn">
              <X className="h-3 w-3 mr-1" /> Clear Chat
            </Button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`flex items-start gap-2.5 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === 'user' ? 'bg-[#0E333F]' : 'bg-slate-100 border border-slate-200'}`}>
                  {msg.role === 'user' ? <User className="h-3.5 w-3.5 text-white" /> : <Bot className="h-3.5 w-3.5 text-slate-600" />}
                </div>
                <div
                  className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${msg.role === 'user' ? 'bg-[#0E333F] text-white rounded-tr-md' : 'bg-white border border-slate-200 text-slate-700 rounded-tl-md'}`}
                  dangerouslySetInnerHTML={{ __html: msg.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }}
                />
              </div>
            </div>
          ))}
          {(loading || uploadingDoc) && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-2xl rounded-tl-md">
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                <span className="text-sm text-slate-500">{uploadingDoc ? 'Analyzing document...' : 'Thinking...'}</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {messages.length <= 1 && (
          <div className="px-4 pb-2 flex gap-2 flex-wrap bg-white border-t border-slate-100">
            {quickActions.map((qa, i) => (
              <button key={i} onClick={() => sendMessage(qa.msg)} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 rounded-full text-xs font-medium text-slate-700 transition-colors border border-slate-200 mt-2" data-testid={`quick-action-${i}`}>
                <qa.icon className="h-3 w-3" />{qa.label}
              </button>
            ))}
          </div>
        )}

        <div className="p-3 border-t bg-white">
          <div className="flex items-center gap-2">
            <input type="file" ref={fileInputRef} onChange={handleDocUpload} className="hidden" accept=".pdf,.txt,.csv,.xlsx,.doc,.docx,.png,.jpg,.jpeg" multiple />
            <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} disabled={loading || uploadingDoc} className="text-slate-400 hover:text-slate-600" data-testid="toolkit-upload-btn"><Upload className="h-4 w-4" /></Button>
            <Input ref={useRef(null)} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask about eligibility, guidelines, calculations..." disabled={loading || uploadingDoc} className="flex-1 border-slate-200 text-slate-800 placeholder:text-slate-400" data-testid="toolkit-chat-input" />
            <Button onClick={() => sendMessage(input)} disabled={!input.trim() || loading || uploadingDoc} size="sm" className="bg-[#0E333F] hover:bg-[#081120]" data-testid="toolkit-send-btn"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      {/* Right: Results Panel */}
      <div className="w-[420px] flex-shrink-0 border border-l-0 border-slate-200 rounded-r-xl bg-white overflow-y-auto">
        {!activeResult ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4"><Sparkles className="h-7 w-7 text-slate-400" /></div>
            <h4 className="text-base font-semibold text-slate-700 mb-2">Tool Results</h4>
            <p className="text-sm text-slate-500 max-w-[280px]">Ask me to evaluate eligibility, analyze credit, or calculate payments — results appear here in real time.</p>
            <div className="mt-6 space-y-2 w-full max-w-[260px]">
              {[
                { icon: Shield, label: 'Pre-Underwriting', color: 'text-indigo-600 bg-indigo-50' },
                { icon: BarChart3, label: 'Credit Analyzer', color: 'text-emerald-600 bg-emerald-50' },
                { icon: Calculator, label: 'Loan Calculator', color: 'text-blue-600 bg-blue-50' },
              ].map((t, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.color}`}><t.icon className="h-4 w-4" /></div>
                  <span className="text-sm font-medium text-slate-700">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        ) : activeResult.type === 'pre_underwriting' ? (
          <PreUnderwritingPanel data={activeResult.data} onExport={() => exportPreUW(activeResult.data)} onSave={openSaveModal} />
        ) : activeResult.type === 'tax_return_analysis' ? (
          <TaxReturnAnalysisPanel data={activeResult.data} onSave={openSaveModal} />
        ) : activeResult.type === 'bank_statement_analysis' ? (
          <BankStatementPanel data={activeResult.data} onSave={openSaveModal} onRunLocAnalysis={() => sendMessage(LOC_QUALIFICATION_PROMPT)} loading={loading} />
        ) : activeResult.type === 'credit_analyzer' ? (
          <CreditResultPanel data={activeResult.data} onSave={openSaveModal} />
        ) : activeResult.type === 'amortization_calculator' ? (
          <AmortResultPanel data={activeResult.data} params={activeResult.params} onSave={openSaveModal} />
        ) : null}
      </div>

      {/* Save to Application Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" data-testid="save-to-app-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-800">Save to Application</h3>
              <button onClick={() => setShowSaveModal(false)} className="h-9 w-9 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-slate-100 text-slate-600 hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-all duration-200"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">Select an application to attach these results to.</p>
            <select
              value={selectedAppId}
              onChange={(e) => setSelectedAppId(e.target.value)}
              className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-slate-400"
              data-testid="select-application"
            >
              <option value="">Select application...</option>
              {applications.map(app => (
                <option key={app.id} value={app.id}>
                  {app.applicant_name
                    || app.term_loan_data?.legal_corporate_name
                    || app.term_loan_10yr_data?.legal_corporate_name
                    || app.term_loan_3_5yr_data?.business_name
                    || app.term_loc_hybrid_data?.business_name || app.term_loc_hybrid_data?.legal_corporate_name
                    || app.sba_loan_data?.business_legal_name
                    || app.line_of_credit_data?.business_name
                    || app.equipment_financing_data?.business_name
                    || app.jumbo_term_loan_data?.business_legal_name
                    || app.merchant_processing_data?.company_name
                    || app.transportation_factoring_data?.first_name
                    || app.term_loan_data?.first_name
                    || app.term_loan_10yr_data?.first_name
                    || app.term_loan_3_5yr_data?.first_name
                    || app.sba_loan_data?.first_name
                    || app.sba_loan_data?.first_last_name
                    || app.line_of_credit_data?.owner_first_name
                    || app.line_of_credit_data?.owner_name
                    || app.equipment_financing_data?.owner_first_name
                    || app.equipment_financing_data?.owner_name
                    || getForeclosureName(app)
                    || 'Unnamed'} — {(app.product_type || '').replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSaveModal(false)}>Cancel</Button>
              <Button onClick={handleSaveToApp} disabled={!selectedAppId || savingToApp} className="bg-[#0E333F] hover:bg-[#081120]" data-testid="confirm-save-btn">
                {savingToApp ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ---- Pre-Underwriting Result Panel ----
const exportPreUW = (data) => {
  const sections = [];
  const PROGRAM_LABELS = {
    line_of_credit: 'Line of Credit',
    growth_term_loan: 'Growth Term Loan',
    term_loan_3_5yr: 'Business Term Loan (3-5 Year)',
    jumbo_rbf: 'Quidity Jumbo RBF',
    jumbo_term_loan: 'Quidity Jumbo Term Loan',
    both: 'Line of Credit & Growth Term Loan',
  };
  const loanLabel = PROGRAM_LABELS[data.loan_type] || 'Pre-Underwriting Assessment';
  sections.push({ heading: `Pre-Underwriting: ${loanLabel}`, rows: [] });
  if (data.summary) sections.push({ heading: 'Summary', rows: [{ label: '', value: data.summary }] });
  if (data.guidelines?.met?.length) sections.push({ heading: `Met Guidelines (${data.guidelines.met.length})`, items: data.guidelines.met.map(g => ({ type: 'met', text: `${g.guideline}: ${g.detail || ''}${g.value ? ` (${g.value})` : ''}` })) });
  if (data.guidelines?.unmet?.length) sections.push({ heading: `Unmet Guidelines (${data.guidelines.unmet.length})`, items: data.guidelines.unmet.map(g => ({ type: 'negative', text: `${g.guideline}: ${g.detail || ''}${g.value ? ` (${g.value})` : ''}` })) });
  if (data.guidelines?.cannot_verify?.length) sections.push({ heading: `Cannot Verify (${data.guidelines.cannot_verify.length})`, items: data.guidelines.cannot_verify.map(g => ({ type: 'cannot_verify', text: `${g.guideline}: ${g.detail || ''}` })) });
  if (data.max_loan_amount) sections.push({ heading: 'Max Loan Amount', rows: [{ label: 'Calculated Maximum', value: `$${Number(data.max_loan_amount).toLocaleString()}` }] });
  generatePDF('Pre-Underwriting Assessment', sections);
};

const PreUnderwritingPanel = ({ data, onExport, onSave }) => {
  const guidelines = data.guidelines || {};
  const met = guidelines.met || [];
  const unmet = guidelines.unmet || [];
  const cannotVerify = guidelines.cannot_verify || [];
  const total = met.length + unmet.length + cannotVerify.length;
  const PROGRAM_LABELS = {
    line_of_credit: 'Line of Credit',
    growth_term_loan: 'Growth Term Loan',
    term_loan_3_5yr: 'Business Term Loan (3-5 Year)',
    jumbo_rbf: 'Quidity Jumbo RBF',
    jumbo_term_loan: 'Quidity Jumbo Term Loan',
    both: 'Eligibility Assessment',
  };
  const loanLabel = PROGRAM_LABELS[data.loan_type] || 'Eligibility Assessment';

  const eligible = unmet.length === 0 && met.length > 0;
  const partial = unmet.length > 0 && met.length > 0;

  return (
    <div className="p-5 space-y-4" data-testid="pre-uw-result-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-indigo-600" />
          <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Pre-Underwriting</h4>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onSave} className="text-slate-400 hover:text-slate-700" data-testid="pre-uw-save-btn"><Save className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" onClick={onExport} className="text-slate-400 hover:text-slate-700" data-testid="pre-uw-export-pdf"><Download className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {/* Status Badge */}
      <div className="text-center p-4 rounded-xl border border-slate-200 bg-slate-50">
        <Badge className="text-xs">{loanLabel}</Badge>
        <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-white text-xs font-bold uppercase mt-3 ${eligible ? 'bg-emerald-500' : partial ? 'bg-amber-500' : 'bg-red-500'}`}>
          {eligible ? <CheckCircle className="h-3.5 w-3.5" /> : partial ? <AlertTriangle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
          {eligible ? 'Eligible' : partial ? 'Partially Eligible' : 'Not Eligible'}
        </div>
        {data.max_loan_amount && (
          <div className="mt-3">
            <span className="text-xs text-slate-500">Max Loan Amount</span>
            <div className="text-2xl font-black text-slate-800">${Number(data.max_loan_amount).toLocaleString()}</div>
          </div>
        )}
      </div>

      {/* Summary */}
      {data.summary && (
        <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
          <p className="text-xs text-slate-600 leading-relaxed">{data.summary}</p>
        </div>
      )}

      {/* Guideline Counts */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-100">
          <div className="text-lg font-bold text-emerald-700">{met.length}</div>
          <div className="text-[10px] font-medium text-emerald-600 uppercase">Met</div>
        </div>
        <div className="p-2 rounded-lg bg-red-50 border border-red-100">
          <div className="text-lg font-bold text-red-700">{unmet.length}</div>
          <div className="text-[10px] font-medium text-red-600 uppercase">Unmet</div>
        </div>
        <div className="p-2 rounded-lg bg-slate-50 border border-slate-200">
          <div className="text-lg font-bold text-slate-700">{cannotVerify.length}</div>
          <div className="text-[10px] font-medium text-slate-500 uppercase">Unverified</div>
        </div>
      </div>

      {/* Met Guidelines */}
      {met.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-emerald-700 uppercase mb-2 flex items-center gap-1">
            <CheckCircle className="h-3 w-3" /> Met ({met.length})
          </h5>
          <div className="space-y-1">
            {met.map((g, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-slate-600 py-1.5 px-2.5 bg-emerald-50 rounded">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-1" />
                <div>
                  <span className="font-medium text-emerald-800">{g.guideline}</span>
                  {g.detail && <span className="text-emerald-600"> — {g.detail}</span>}
                  {g.value && <span className="text-emerald-500"> ({g.value})</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unmet Guidelines */}
      {unmet.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-red-700 uppercase mb-2 flex items-center gap-1">
            <XCircle className="h-3 w-3" /> Unmet ({unmet.length})
          </h5>
          <div className="space-y-1">
            {unmet.map((g, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-slate-600 py-1.5 px-2.5 bg-red-50 rounded">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0 mt-1" />
                <div>
                  <span className="font-medium text-red-800">{g.guideline}</span>
                  {g.detail && <span className="text-red-600"> — {g.detail}</span>}
                  {g.value && <span className="text-red-500"> ({g.value})</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cannot Verify */}
      {cannotVerify.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-slate-600 uppercase mb-2 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Cannot Verify ({cannotVerify.length})
          </h5>
          <div className="space-y-1">
            {cannotVerify.map((g, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-slate-500 py-1.5 px-2.5 bg-slate-50 rounded border border-slate-200">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-400 flex-shrink-0 mt-1" />
                <div>
                  <span className="font-medium text-slate-700">{g.guideline}</span>
                  {g.detail && <span> — {g.detail}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Credit Result Panel ----
const CreditResultPanel = ({ data, onSave }) => {
  const { totalScore, riskLevel, riskColor, positives, negatives } = data;
  const exportPDF = () => {
    generatePDF('Business Credit Analysis', [
      { heading: 'Overall Assessment', rows: [{ label: 'Readiness Score', value: `${totalScore}/100` }, { label: 'Risk Level', value: riskLevel.toUpperCase() }] },
      { heading: `Strengths (${positives.length})`, items: positives.map(p => ({ type: 'positive', text: p })) },
      { heading: `Concerns (${negatives.length})`, items: negatives.map(n => ({ type: 'negative', text: n })) },
    ]);
  };
  return (
    <div className="p-5 space-y-4" data-testid="credit-result-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-slate-600" /><h4 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Credit Analysis</h4></div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onSave} className="text-slate-400 hover:text-slate-700" data-testid="credit-save-btn"><Save className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" onClick={exportPDF} className="text-slate-400 hover:text-slate-700" data-testid="credit-export-pdf"><Download className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      <div className="text-center p-5 rounded-xl border border-slate-200 bg-slate-50">
        <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-white text-xs font-bold uppercase ${riskColor}`}>
          {riskLevel === 'low' ? <CheckCircle className="h-3.5 w-3.5" /> : riskLevel === 'medium' ? <AlertTriangle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
          {riskLevel} Risk
        </div>
        <div className="text-4xl font-black text-slate-800 mt-3">{totalScore}<span className="text-lg text-slate-400">/100</span></div>
        <p className="text-xs text-slate-500 mt-1">Readiness Score</p>
      </div>
      <div>
        <h5 className="text-xs font-semibold text-emerald-700 uppercase mb-2 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Strengths ({positives.length})</h5>
        <div className="space-y-1">{positives.map((p, i) => (<div key={i} className="flex items-center gap-2 text-xs text-slate-600 py-1 px-2 bg-emerald-50 rounded"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />{p}</div>))}</div>
      </div>
      <div>
        <h5 className="text-xs font-semibold text-red-700 uppercase mb-2 flex items-center gap-1"><XCircle className="h-3 w-3" /> Concerns ({negatives.length})</h5>
        <div className="space-y-1">{negatives.map((n, i) => (<div key={i} className="flex items-center gap-2 text-xs text-slate-600 py-1 px-2 bg-red-50 rounded"><div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />{n}</div>))}</div>
      </div>
    </div>
  );
};

// ---- Amort Result Panel ----
const AmortResultPanel = ({ data, params, onSave }) => {
  const loanLabels = { term_loan: 'Term Loan', sba_loan: 'SBA Loan', line_of_credit: 'Line of Credit', jumbo_term_loan: 'Quidity Jumbo Term Loan', jumbo_rbf: 'Quidity Jumbo RBF' };
  const fmt = (v) => typeof v === 'number' ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v) : v;
  const exportPDF = () => {
    generatePDF('Loan Amortization Report', [
      { heading: 'Loan Details', rows: [
        { label: 'Loan Type', value: loanLabels[params?.loan_type] || 'Loan' }, { label: 'Loan Amount', value: fmt(params?.amount || 0) },
        { label: 'Monthly Payment', value: data.monthlyPayment }, { label: 'Total Interest', value: data.totalInterest },
        { label: 'Total Repayment', value: data.totalRepayment }, { label: 'Terms', value: data.detail },
      ] },
    ]);
  };
  return (
    <div className="p-5 space-y-4" data-testid="amort-result-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><Calculator className="h-4 w-4 text-slate-600" /><h4 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Loan Calculator</h4></div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onSave} className="text-slate-400 hover:text-slate-700" data-testid="amort-save-btn"><Save className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" onClick={exportPDF} className="text-slate-400 hover:text-slate-700" data-testid="amort-export-pdf"><Download className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      <div className="text-center p-5 rounded-xl border border-slate-200 bg-slate-50">
        <Badge className="bg-blue-100 text-blue-800 text-xs mb-2">{loanLabels[params?.loan_type] || 'Loan'}</Badge>
        <div className="text-3xl font-black text-slate-800 mt-2">{data.monthlyPayment}</div>
        <p className="text-xs text-slate-500 mt-1">Monthly Payment</p>
      </div>
      <div className="space-y-2.5">
        <div className="flex justify-between items-center py-2.5 px-3 bg-slate-50 rounded-lg"><span className="text-xs font-medium text-slate-500">Loan Amount</span><span className="text-sm font-bold text-slate-800">{fmt(params?.amount || 0)}</span></div>
        <div className="flex justify-between items-center py-2.5 px-3 bg-slate-50 rounded-lg"><span className="text-xs font-medium text-slate-500">Total Interest</span><span className="text-sm font-bold text-slate-800">{data.totalInterest}</span></div>
        <div className="flex justify-between items-center py-2.5 px-3 bg-slate-50 rounded-lg"><span className="text-xs font-medium text-slate-500">Total Repayment</span><span className="text-sm font-bold text-slate-800">{data.totalRepayment}</span></div>
        <div className="text-center text-xs text-slate-500 pt-1">{data.detail}</div>
      </div>
    </div>
  );
};

// ---- Bank Statement Analysis Panel ----
const BankStatementPanel = ({ data, onSave, onRunLocAnalysis, loading }) => {
  const fmt = (v) => {
    if (v === null || v === undefined) return '—';
    const n = typeof v === 'string' ? parseFloat(v.replace(/[,$]/g, '')) : Number(v);
    if (isNaN(n)) return '—';
    const abs = Math.abs(n);
    const formatted = abs >= 1000 ? `$${Math.round(abs).toLocaleString()}` : `$${abs.toFixed(2)}`;
    return n < 0 ? `-${formatted}` : formatted;
  };
  const months = data?.months || [];
  const totalNsf = data?.total_nsf ?? months.reduce((s, m) => s + (m.nsf_count || 0), 0);
  const avgBalance = data?.average_balance;
  const accountHolder = data?.account_holder || data?.business_name;
  const bankName = data?.bank_name;

  const exportPDF = () => {
    const totalDeposits = data?.total_deposits ?? months.reduce((s, m) => s + (m.total_deposits || 0), 0);
    const totalWithdrawals = data?.total_withdrawals ?? months.reduce((s, m) => s + (m.total_withdrawals || 0), 0);
    const sections = [
      { heading: `Bank Statement Analysis${accountHolder ? ' — ' + accountHolder : ''}${bankName ? ' (' + bankName + ')' : ''}`, rows: [
        { label: 'Months Analyzed', value: String(months.length) },
        { label: 'Average Daily Balance', value: fmt(avgBalance) },
        { label: 'Total NSFs', value: String(totalNsf) },
      ]},
      ...(months.length > 0 ? [{ heading: 'Monthly Breakdown', rows: months.flatMap((m, i) => {
        const netChange = (m.total_deposits || 0) - (m.total_withdrawals || 0);
        const label = m.month_year || `Month ${i + 1}`;
        return [
          { label: `${label}  Beginning Balance`, value: fmt(m.beginning_balance) },
          { label: `${label}  Ending Balance`, value: fmt(m.ending_balance) },
          { label: `${label}  Total Deposits`, value: fmt(m.total_deposits) },
          { label: `${label}  Total Withdrawals`, value: fmt(m.total_withdrawals) },
          { label: `${label}  Avg Daily Balance`, value: fmt(m.average_daily_balance) },
          { label: `${label}  Net Change`, value: fmt(netChange) },
          { label: `${label}  NSF Count`, value: String(m.nsf_count || 0) },
        ];
      })}] : []),
      ...(months.length > 1 ? [{ heading: `Aggregate (${months.length} Months)`, rows: [
        { label: 'Total Deposits', value: fmt(totalDeposits) },
        { label: 'Total Withdrawals', value: fmt(totalWithdrawals) },
        { label: 'Net Cash Flow', value: fmt(totalDeposits - totalWithdrawals) },
        { label: 'Avg Monthly Balance', value: fmt(avgBalance) },
      ]}] : []),
      { heading: 'Disclaimer', rows: [{ label: '', value: 'This analysis is extracted from uploaded bank statements. Verify all figures against original documents.' }] },
    ];
    generatePDF('Bank Statement Analysis', sections);
  };

  return (
    <div className="p-5 space-y-4 overflow-y-auto" data-testid="bank-statement-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-slate-600" />
          <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Bank Statement Analysis</h4>
        </div>
        <div className="flex items-center gap-1">
          {onSave && (
            <Button variant="ghost" size="sm" onClick={onSave} className="text-slate-400 hover:text-slate-700" data-testid="bank-save-btn"><Save className="h-3.5 w-3.5" /></Button>
          )}
          <Button variant="ghost" size="sm" onClick={exportPDF} className="text-slate-400 hover:text-slate-700" data-testid="bank-export-pdf"><Download className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {/* Header info */}
      {(accountHolder || bankName) && (
        <div className="text-center p-3 bg-slate-50 rounded-xl border border-slate-200">
          {accountHolder && <p className="text-sm font-bold text-slate-800">{accountHolder}</p>}
          {bankName && <p className="text-xs text-slate-500">{bankName}</p>}
          <Badge className="bg-blue-100 text-blue-800 text-xs mt-1">{months.length} {months.length === 1 ? 'Month' : 'Months'} Analyzed</Badge>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-center">
          <p className="text-xs text-slate-500 font-medium">Avg Daily Balance</p>
          <p className="text-lg font-black text-slate-800">{fmt(avgBalance)}</p>
        </div>
        <div className={`p-3 rounded-lg border text-center ${totalNsf > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
          <p className="text-xs text-slate-500 font-medium">Total NSFs</p>
          <p className={`text-lg font-black ${totalNsf > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{totalNsf}</p>
        </div>
      </div>

      {/* Monthly breakdown */}
      {months.length > 0 && (
        <div>
          <h5 className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Monthly Breakdown</h5>
          <div className="space-y-2">
            {months.map((m, i) => {
              const netChange = (m.total_deposits || 0) - (m.total_withdrawals || 0);
              return (
                <div key={i} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`bank-month-${i}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-bold text-slate-700">{m.month_year || `Month ${i + 1}`}</span>
                    {m.nsf_count > 0 && <Badge className="bg-red-100 text-red-700 text-[10px]">{m.nsf_count} NSF</Badge>}
                    {m.nsf_count === 0 && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">No NSF</Badge>}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <div className="flex justify-between"><span className="text-slate-500">Beginning Bal</span><span className="font-medium text-slate-700">{fmt(m.beginning_balance)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Ending Bal</span><span className="font-medium text-slate-700">{fmt(m.ending_balance)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Deposits</span><span className="font-medium text-emerald-700">{fmt(m.total_deposits)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Withdrawals</span><span className="font-medium text-red-700">{fmt(m.total_withdrawals)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Avg Daily Bal</span><span className="font-medium text-slate-700">{fmt(m.average_daily_balance)}</span></div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Net Income/Loss</span>
                      <span className={`font-bold ${netChange >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(netChange)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Aggregate totals */}
      {months.length > 1 && (
        <div className="border-t border-slate-200 pt-3">
          <h5 className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Aggregate ({months.length} Months)</h5>
          <div className="space-y-1.5">
            {[
              { label: 'Total Deposits', value: fmt(data?.total_deposits ?? months.reduce((s, m) => s + (m.total_deposits || 0), 0)), color: 'text-emerald-700' },
              { label: 'Total Withdrawals', value: fmt(data?.total_withdrawals ?? months.reduce((s, m) => s + (m.total_withdrawals || 0), 0)), color: 'text-red-700' },
              { label: 'Net Cash Flow', value: fmt((data?.total_deposits ?? months.reduce((s, m) => s + (m.total_deposits || 0), 0)) - (data?.total_withdrawals ?? months.reduce((s, m) => s + (m.total_withdrawals || 0), 0))), color: 'text-slate-800' },
              { label: 'Avg Monthly Balance', value: fmt(avgBalance), color: 'text-slate-800' },
            ].map((row, i) => (
              <div key={i} className="flex justify-between items-center py-2 px-3 bg-slate-50 rounded-lg">
                <span className="text-xs font-medium text-slate-500">{row.label}</span>
                <span className={`text-sm font-bold ${row.color}`}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {onRunLocAnalysis && (
        <div className="pt-2">
          <Button
            onClick={onRunLocAnalysis}
            disabled={loading}
            className="w-full bg-teal-700 hover:bg-teal-800 text-white font-semibold"
            size="sm"
            data-testid="run-loc-qualification-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-2" />}
            Run LOC Qualification Analysis
          </Button>
        </div>
      )}

      <p className="text-[10px] text-slate-400 italic pt-2 border-t border-slate-100">
        This analysis is extracted from uploaded bank statements. Verify all figures against original documents.
      </p>
    </div>
  );
};

// ---- Tax Return Analysis Panel ----
const TaxReturnAnalysisPanel = ({ data, onSave }) => {
  const fmt = (v) => {
    if (v === null || v === undefined) return '—';
    const n = typeof v === 'string' ? parseFloat(v.replace(/[,$]/g, '')) : Number(v);
    if (isNaN(n)) return '—';
    const abs = Math.abs(n);
    const formatted = abs >= 1000 ? `$${Math.round(abs).toLocaleString()}` : `$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return n < 0 ? `-${formatted}` : formatted;
  };
  const num = (v) => {
    if (v === null || v === undefined) return 0;
    const n = typeof v === 'string' ? parseFloat(v.replace(/[,$]/g, '')) : Number(v);
    return isNaN(n) ? 0 : n;
  };

  // Extract key figures
  const revenue = num(data.gross_receipts_or_revenue);
  const cogs = num(data.cost_of_goods_sold);
  const grossProfit = num(data.gross_profit) || (revenue - cogs);
  const officerComp = num(data.officer_compensation);
  const guaranteedPayments = num(data.guaranteed_payments_to_partners);
  const interestExp = num(data.interest_expense);
  const depreciation = num(data.depreciation);
  const netIncome = num(data.net_income_or_loss);
  const existingDebt = num(data.existing_annual_debt_service);
  const formType = String(data.form_type || '').toLowerCase();

  // Textbook EBITDA = Net Income + Interest + Taxes + Depreciation + Amortization.
  // (We don't currently extract entity-level income tax — 1065/1120-S/Schedule C
  // pay no entity tax — or amortization separately, so this is NI + Int + Depr.)
  const ebitda = netIncome + interestExp + depreciation;

  // Adjusted EBITDA (a.k.a. Cash Flow Available for Debt Service) adds an owner-pay
  // add-back. Used for DSCR sizing because the owner can dial back comp to service
  // new debt. Form-type-aware:
  //   - 1065 partnership: ONLY guaranteed_payments_to_partners (Line 10). We
  //     deliberately IGNORE officer_compensation for 1065 because the AI sometimes
  //     drops Line 9 Salaries into it and that would inflate cash flow by $1M+.
  //   - Schedule C / 1040: owner pay is already in net income — no add-back.
  //   - 1120 / 1120-S: officer_compensation (Line 12 / Line 7 of those forms).
  const isPartnership = formType.includes('1065') || formType.includes('partner');
  const isSoleProp = formType.includes('schedule c') || (formType.includes('1040') && !formType.includes('1041'));
  let ownerCompAddBack;
  if (isPartnership) {
    ownerCompAddBack = guaranteedPayments;
  } else if (isSoleProp) {
    ownerCompAddBack = 0;
  } else {
    ownerCompAddBack = officerComp;
  }
  const adjustedEbitda = ebitda + ownerCompAddBack;

  // DSCR calculations at 18% interest
  const annualRate = 0.18;
  const monthlyRate = annualRate / 12;

  // Compute monthly payment for a given loan at 18%
  const calcMonthlyPayment = (principal, termMonths) => {
    if (principal <= 0 || termMonths <= 0) return 0;
    return principal * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / (Math.pow(1 + monthlyRate, termMonths) - 1);
  };

  // Compute max loan from DSCR constraint
  const calcMaxLoanFromDSCR = (minDSCR, termMonths) => {
    const maxAnnualDebtService = adjustedEbitda / minDSCR;
    const availableAnnual = maxAnnualDebtService - existingDebt;
    if (availableAnnual <= 0) return 0;
    const maxMonthly = availableAnnual / 12;
    // PV of annuity: PV = PMT * [(1 - (1+r)^-n) / r]
    const pvFactor = (1 - Math.pow(1 + monthlyRate, -termMonths)) / monthlyRate;
    return maxMonthly * pvFactor;
  };

  // 10 Year Term Loan (Growth Term Loan)
  // Guidelines: 1.25x DSCR, max 60% revenue less current debt, max $200K
  const maxLoan10yr_dscr = calcMaxLoanFromDSCR(1.25, 120);
  const maxLoan10yr_revenue = revenue * 0.60 - existingDebt; // 60% of revenue - current debt
  const maxLoan10yr = Math.max(0, Math.min(maxLoan10yr_dscr, maxLoan10yr_revenue > 0 ? maxLoan10yr_revenue : Infinity, 200000));

  // 3-5 Year Term Loan
  // Guidelines: 1.15 business DSCR, max 25% prior year revenue, max $300K
  const maxLoan35yr_dscr = calcMaxLoanFromDSCR(1.15, 60);
  const maxLoan35yr_revenue = revenue * 0.25; // 25% of prior year revenue
  const maxLoan35yr = Math.max(0, Math.min(maxLoan35yr_dscr, maxLoan35yr_revenue > 0 ? maxLoan35yr_revenue : Infinity, 300000));

  // ---- Quidity Jumbo Term Loan (24% simple interest, 24-month term) ----
  // Guidelines: 1.25x DSCR floor, $1.8M annual revenue floor,
  // 3%+ EBITDA margin, hard cap $10M. Revenue cap of 50% of annual revenue.
  // For simple-interest sizing:  annual_debt_service = principal × (1 + r·t) × (12/n)
  //   where r=0.24, t=2 yrs, n=24 months  =>  annual = principal × 1.48 × (12/24) = 0.74 × principal
  const calcMaxLoanSimpleInterest = (minDSCR, annualRate, termMonths) => {
    const maxAnnualDebtService = adjustedEbitda / minDSCR;
    const availableAnnual = maxAnnualDebtService - existingDebt;
    if (availableAnnual <= 0) return 0;
    // Total repayment over term = principal × (1 + r × years); years = termMonths/12
    const totalFactor = 1 + annualRate * (termMonths / 12);
    // Annual debt service for a simple-interest loan paid evenly over termMonths
    // = principal × totalFactor × (12 / termMonths)
    const annualFactor = totalFactor * (12 / termMonths);
    return availableAnnual / annualFactor;
  };
  const maxJumboTL_dscr = calcMaxLoanSimpleInterest(1.25, 0.24, 24);
  const maxJumboTL_revenue = revenue * 0.50; // 50% of annual revenue
  const maxJumboTL = Math.max(0, Math.min(
    maxJumboTL_dscr,
    maxJumboTL_revenue > 0 ? maxJumboTL_revenue : Infinity,
    10000000
  ));
  // EBITDA margin uses the TRUE (textbook) ebitda — quality-of-earnings signal
  // that shouldn't be inflated by owner-comp add-backs.
  const ebitdaMargin = revenue > 0 ? (ebitda / revenue) : 0;
  const qualifiesJumboTL = (
    maxJumboTL > 25000 &&
    adjustedEbitda > 0 &&
    revenue >= 3000000 &&     // $250K/mo × 12
    ebitdaMargin >= 0.03      // 3%+ EBITDA margin (textbook EBITDA)
  );

  // Simple-interest monthly payment helper
  const calcSimpleInterestMonthly = (principal, annualRate, termMonths) => {
    if (principal <= 0 || termMonths <= 0) return 0;
    const totalRepay = principal * (1 + annualRate * (termMonths / 12));
    return totalRepay / termMonths;
  };
  const monthlyPmtJumboTL = calcSimpleInterestMonthly(maxJumboTL, 0.24, 24);
  const dscrJumboTL = (existingDebt + monthlyPmtJumboTL * 12) > 0
    ? adjustedEbitda / (existingDebt + monthlyPmtJumboTL * 12)
    : null;

  // ---- Quidity Jumbo RBF (24% simple interest, 12-month term) ----
  const maxJumboRBF_revenue = revenue * 0.50;
  // Cash-flow upper bound: principal whose annual debt service ≤ Adjusted EBITDA
  const annualizedRBF_cap = adjustedEbitda > 0 ? adjustedEbitda / 1.24 : 0;
  const maxJumboRBF = Math.max(0, Math.min(
    maxJumboRBF_revenue > 0 ? maxJumboRBF_revenue : Infinity,
    annualizedRBF_cap > 0 ? annualizedRBF_cap : Infinity,
    10000000
  ));
  const qualifiesJumboRBF = (
    maxJumboRBF > 25000 &&
    adjustedEbitda > 0 &&
    revenue >= 1800000    // $1.8M annual revenue minimum
  );
  const monthlyPmtJumboRBF = calcSimpleInterestMonthly(maxJumboRBF, 0.24, 12);
  const dscrJumboRBF = (existingDebt + monthlyPmtJumboRBF * 12) > 0
    ? adjustedEbitda / (existingDebt + monthlyPmtJumboRBF * 12)
    : null;

  // ===== DSCR / sizing uses ADJUSTED EBITDA =====
  // Per banking convention: DSCR = Cash Flow Available for Debt Service / Debt
  // Service. The numerator includes owner-pay add-backs (officer comp /
  // guaranteed payments) because owners can dial back comp to service debt.
  // We use `adjustedEbitda` here, NEVER plain `ebitda`.

  // Monthly payments for the max loans
  const monthlyPmt10yr = calcMonthlyPayment(maxLoan10yr, 120);
  const monthlyPmt35yr = calcMonthlyPayment(maxLoan35yr, 60);

  // Current DSCR (without new loan)
  const currentDSCR = existingDebt > 0 ? adjustedEbitda / existingDebt : null;

  // DSCR with max loan at each program
  const dscr10yr = (existingDebt + monthlyPmt10yr * 12) > 0 ? adjustedEbitda / (existingDebt + monthlyPmt10yr * 12) : null;
  const dscr35yr = (existingDebt + monthlyPmt35yr * 12) > 0 ? adjustedEbitda / (existingDebt + monthlyPmt35yr * 12) : null;

  // Qualification determination
  const qualifies10yr = maxLoan10yr > 25000 && adjustedEbitda > 0 && revenue >= 100000;
  const qualifies35yr = maxLoan35yr > 10000 && adjustedEbitda > 0;

  const FIELDS = [
    { label: 'Business Name', value: data.business_name, raw: true },
    { label: 'Tax Year', value: data.tax_year, raw: true },
    { label: 'Form Type', value: data.form_type, raw: true },
    { label: 'Gross Revenue', value: revenue, highlight: true },
    { label: 'Cost of Goods Sold', value: cogs },
    { label: 'Gross Profit', value: grossProfit },
    { label: 'Officer Compensation', value: officerComp },
    { label: 'Interest Expense', value: interestExp },
    { label: 'Depreciation', value: depreciation },
    { label: 'Net Income/Loss', value: netIncome, highlight: true },
  ];

  const exportPDF = () => {
    const sections = [
      { heading: `Tax Return Analysis — ${data.business_name || 'Unknown'} (${data.tax_year || 'N/A'})`, rows: FIELDS.filter(f => !f.raw || f.value).map(f => ({ label: f.label, value: f.raw ? (f.value || '—') : fmt(f.value) })) },
      { heading: 'DSCR Analysis (at 18% Interest)', rows: [
        { label: 'EBITDA (Net Income + Interest + Depreciation)', value: fmt(ebitda) },
        { label: 'Owner Compensation Add-back', value: fmt(ownerCompAddBack) },
        { label: 'Adjusted EBITDA (Cash Flow Available for Debt Service)', value: fmt(adjustedEbitda) },
        { label: 'Existing Annual Debt Service', value: fmt(existingDebt) },
        { label: 'Current DSCR (Adj. EBITDA / Existing Debt)', value: currentDSCR ? currentDSCR.toFixed(2) + 'x' : 'N/A (no existing debt)' },
      ]},
      { heading: '10 Year Term Loan Eligibility', rows: [
        { label: 'Max Loan Amount', value: fmt(maxLoan10yr) },
        { label: 'Monthly Payment', value: fmt(monthlyPmt10yr) },
        { label: 'Projected DSCR', value: dscr10yr ? dscr10yr.toFixed(2) + 'x' : 'N/A' },
        { label: 'Status', value: qualifies10yr ? 'Potentially Eligible' : 'Does Not Qualify' },
      ]},
      { heading: '3-5 Year Term Loan Eligibility', rows: [
        { label: 'Max Loan Amount', value: fmt(maxLoan35yr) },
        { label: 'Monthly Payment', value: fmt(monthlyPmt35yr) },
        { label: 'Projected DSCR', value: dscr35yr ? dscr35yr.toFixed(2) + 'x' : 'N/A' },
        { label: 'Status', value: qualifies35yr ? 'Potentially Eligible' : 'Does Not Qualify' },
      ]},
      { heading: 'Quidity Jumbo Term Loan Eligibility (24% simple / 24 mo)', rows: [
        { label: 'Max Loan Amount', value: fmt(maxJumboTL) },
        { label: 'Monthly Payment', value: fmt(monthlyPmtJumboTL) },
        { label: 'Projected DSCR', value: dscrJumboTL ? dscrJumboTL.toFixed(2) + 'x' : 'N/A' },
        { label: 'EBITDA Margin', value: revenue > 0 ? `${(ebitdaMargin * 100).toFixed(1)}%` : 'N/A' },
        { label: 'Status', value: qualifiesJumboTL ? 'Potentially Eligible' : 'Does Not Qualify' },
      ]},
      { heading: 'Quidity Jumbo RBF Eligibility (24% simple / 12 mo)', rows: [
        { label: 'Max Purchase Amount', value: fmt(maxJumboRBF) },
        { label: 'Monthly Payment', value: fmt(monthlyPmtJumboRBF) },
        { label: 'Projected DSCR', value: dscrJumboRBF ? dscrJumboRBF.toFixed(2) + 'x' : 'N/A' },
        { label: 'Status', value: qualifiesJumboRBF ? 'Potentially Eligible' : 'Does Not Qualify' },
      ]},
      (() => {
        // SBA Above $350K sizing (11.25% / 10-yr) uses Adjusted EBITDA
        const r = 0.1125, n = 120;
        const pvFactor = (1 - Math.pow(1 + r/12, -n)) / (r/12);
        const businessMaxADS = adjustedEbitda / 1.15;
        const businessAvailable = businessMaxADS - existingDebt;
        const businessCap = businessAvailable > 0 ? (businessAvailable / 12) * pvFactor : 0;
        const revenueCap = revenue * 0.35;
        const rawMax = Math.max(0, Math.min(businessCap, revenueCap));
        const sbaMax = Math.min(5000000, rawMax);
        const meetsFloor = sbaMax >= 350000;
        return {
          heading: 'SBA Loan Eligibility — Above $350K (up to $5M)',
          rows: [
            { label: 'Max Loan Amount', value: fmt(sbaMax) },
            { label: 'Business DSCR Cap (1.15x, Adj. EBITDA)', value: fmt(Math.max(0, businessCap)) },
            { label: 'Global DSCR Cap (1.00x)', value: 'Pending PFS data' },
            { label: 'Revenue Cap (35%)', value: fmt(Math.max(0, revenueCap)) },
            { label: 'Min TIB', value: '3 yrs (4 yrs construction)' },
            { label: 'Min Revenue', value: '$100,000' },
            { label: 'Status', value: (meetsFloor && revenue >= 100000 && adjustedEbitda > 0) ? 'Potentially Eligible' : 'Does Not Qualify' },
          ],
        };
      })(),
      { heading: 'Disclaimer', rows: [{ label: '', value: 'EBITDA = Net Income + Interest + Taxes + Depreciation + Amortization (textbook). Adjusted EBITDA adds back owner compensation as a discretionary add-back representing Cash Flow Available for Debt Service. DSCR sizing uses Adjusted EBITDA. Borrower must still credit qualify (FICO, time in business, etc.).' }] },
    ];
    generatePDF('Tax Return DSCR Analysis', sections);
  };

  return (
    <div className="p-5 space-y-4 overflow-y-auto" data-testid="tax-return-analysis-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-indigo-600" />
          <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Tax Return Analysis</h4>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onSave} className="text-slate-400 hover:text-slate-700" data-testid="tax-save-btn"><Save className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" onClick={exportPDF} className="text-slate-400 hover:text-slate-700" data-testid="tax-export-pdf"><Download className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {/* Business Info */}
      {(data.business_name || data.tax_year) && (
        <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-center">
          {data.business_name && <p className="text-sm font-bold text-slate-800">{data.business_name}</p>}
          <div className="flex items-center justify-center gap-2 mt-1">
            {data.tax_year && <Badge variant="outline" className="text-xs">{data.tax_year}</Badge>}
            {data.form_type && <Badge variant="outline" className="text-xs">{data.form_type}</Badge>}
          </div>
        </div>
      )}

      {/* Extracted Financial Fields */}
      <div>
        <h5 className="text-xs font-semibold text-slate-600 uppercase mb-2 flex items-center gap-1">
          <DollarSign className="h-3 w-3" /> Extracted Financials
        </h5>
        <div className="space-y-1">
          {FIELDS.filter(f => !f.raw).map((f, i) => (
            <div key={i} className={`flex justify-between items-center py-1.5 px-2.5 rounded text-xs ${f.highlight ? 'bg-indigo-50 border border-indigo-100' : 'bg-slate-50'}`}>
              <span className="text-slate-600 font-medium">{f.label}</span>
              <span className={`font-bold ${f.value < 0 ? 'text-red-600' : 'text-slate-800'}`} data-testid={`tax-field-${f.label.replace(/\s/g, '-').toLowerCase()}`}>{fmt(f.value)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* EBITDA / DSCR */}
      <div className="p-3 rounded-xl border border-slate-200 bg-slate-50">
        <h5 className="text-xs font-semibold text-slate-600 uppercase mb-3 flex items-center gap-1">
          <TrendingUp className="h-3 w-3" /> DSCR Analysis <span className="text-slate-400 font-normal">(at 18% interest)</span>
        </h5>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="p-2.5 rounded-lg bg-white border border-slate-200 text-center" title="Net Income + Interest + Taxes + Depreciation + Amortization (textbook EBITDA — no owner-pay add-back)">
            <p className="text-[10px] text-slate-500 font-medium uppercase">EBITDA</p>
            <p className={`text-lg font-black ${ebitda > 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="tax-ebitda">{fmt(ebitda)}</p>
            <p className="text-[9px] text-slate-400 mt-0.5">NI + Int + Depr</p>
          </div>
          <div className="p-2.5 rounded-lg bg-white border border-slate-200 text-center" title="EBITDA + Owner Compensation Add-back (Cash Flow Available for Debt Service)">
            <p className="text-[10px] text-slate-500 font-medium uppercase">Adj. EBITDA</p>
            <p className={`text-lg font-black ${adjustedEbitda > 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="tax-adjusted-ebitda">{fmt(adjustedEbitda)}</p>
            <p className="text-[9px] text-slate-400 mt-0.5">{ownerCompAddBack > 0 ? `+ ${fmt(ownerCompAddBack)} owner pay` : 'No owner add-back'}</p>
          </div>
          <div className="p-2.5 rounded-lg bg-white border border-slate-200 text-center" title="Adjusted EBITDA / Existing Annual Debt Service">
            <p className="text-[10px] text-slate-500 font-medium uppercase">Current DSCR</p>
            <p className={`text-lg font-black ${currentDSCR === null ? 'text-slate-400' : currentDSCR >= 1.25 ? 'text-emerald-700' : currentDSCR >= 1.0 ? 'text-amber-600' : 'text-red-600'}`} data-testid="tax-current-dscr">
              {currentDSCR !== null ? `${currentDSCR.toFixed(2)}x` : 'N/A'}
            </p>
            {currentDSCR === null && <p className="text-[9px] text-slate-400">No existing debt</p>}
          </div>
        </div>
        {existingDebt > 0 && (
          <div className="flex justify-between items-center py-1.5 px-2.5 bg-white rounded text-xs border border-slate-200 mb-2">
            <span className="text-slate-600">Existing Annual Debt Service</span>
            <span className="font-bold text-slate-800">{fmt(existingDebt)}</span>
          </div>
        )}
        <p className="text-[10px] text-slate-500 italic mt-2">
          DSCR sizing uses <span className="font-semibold">Adjusted EBITDA</span> (Cash Flow Available for Debt Service) because owners can reduce comp to service new debt.
        </p>
      </div>

      {/* 10 Year Eligibility */}
      <div className={`p-3 rounded-xl border ${qualifies10yr ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-xs font-bold text-slate-700 uppercase">10 Year Term Loan</h5>
          <Badge className={`text-[10px] ${qualifies10yr ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`} data-testid="tax-10yr-status">
            {qualifies10yr ? 'Potentially Eligible' : 'Does Not Qualify'}
          </Badge>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs"><span className="text-slate-600">Max Loan Amount</span><span className="font-bold text-slate-800" data-testid="tax-10yr-max">{fmt(maxLoan10yr)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Monthly Payment</span><span className="font-bold text-slate-800">{fmt(monthlyPmt10yr)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Projected DSCR</span><span className={`font-bold ${dscr10yr && dscr10yr >= 1.25 ? 'text-emerald-700' : 'text-red-600'}`}>{dscr10yr ? `${dscr10yr.toFixed(2)}x` : 'N/A'}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Min Required DSCR</span><span className="font-medium text-slate-500">1.25x</span></div>
          {revenue > 0 && <div className="flex justify-between text-xs"><span className="text-slate-600">Revenue Cap (60%)</span><span className="font-medium text-slate-500">{fmt(revenue * 0.6)}</span></div>}
        </div>
      </div>

      {/* 3-5 Year Eligibility */}
      <div className={`p-3 rounded-xl border ${qualifies35yr ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-xs font-bold text-slate-700 uppercase">3-5 Year Term Loan</h5>
          <Badge className={`text-[10px] ${qualifies35yr ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`} data-testid="tax-35yr-status">
            {qualifies35yr ? 'Potentially Eligible' : 'Does Not Qualify'}
          </Badge>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs"><span className="text-slate-600">Max Loan Amount</span><span className="font-bold text-slate-800" data-testid="tax-35yr-max">{fmt(maxLoan35yr)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Monthly Payment</span><span className="font-bold text-slate-800">{fmt(monthlyPmt35yr)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Projected DSCR</span><span className={`font-bold ${dscr35yr && dscr35yr >= 1.15 ? 'text-emerald-700' : 'text-red-600'}`}>{dscr35yr ? `${dscr35yr.toFixed(2)}x` : 'N/A'}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Min Required DSCR</span><span className="font-medium text-slate-500">1.15x</span></div>
          {revenue > 0 && <div className="flex justify-between text-xs"><span className="text-slate-600">Revenue Cap (25%)</span><span className="font-medium text-slate-500">{fmt(revenue * 0.25)}</span></div>}
        </div>
      </div>

      {/* Quidity Jumbo Term Loan Eligibility */}
      <div className={`p-3 rounded-xl border ${qualifiesJumboTL ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-xs font-bold text-slate-700 uppercase">Quidity Jumbo Term Loan</h5>
          <Badge className={`text-[10px] ${qualifiesJumboTL ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`} data-testid="tax-jumbo-tl-status">
            {qualifiesJumboTL ? 'Potentially Eligible' : 'Does Not Qualify'}
          </Badge>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs"><span className="text-slate-600">Max Loan Amount</span><span className="font-bold text-slate-800" data-testid="tax-jumbo-tl-max">{fmt(maxJumboTL)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Monthly Payment</span><span className="font-bold text-slate-800">{fmt(monthlyPmtJumboTL)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Projected DSCR</span><span className={`font-bold ${dscrJumboTL && dscrJumboTL >= 1.25 ? 'text-emerald-700' : 'text-red-600'}`}>{dscrJumboTL ? `${dscrJumboTL.toFixed(2)}x` : 'N/A'}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Min Required DSCR</span><span className="font-medium text-slate-500">1.25x</span></div>
          {revenue > 0 && <div className="flex justify-between text-xs"><span className="text-slate-600">Revenue Cap (50%)</span><span className="font-medium text-slate-500">{fmt(revenue * 0.5)}</span></div>}
          <div className="flex justify-between text-xs"><span className="text-slate-600">EBITDA Margin</span><span className={`font-bold ${ebitdaMargin >= 0.03 ? 'text-emerald-700' : 'text-red-600'}`}>{revenue > 0 ? `${(ebitdaMargin * 100).toFixed(1)}%` : 'N/A'}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Min EBITDA Margin</span><span className="font-medium text-slate-500">3.0%</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Rate / Term</span><span className="font-medium text-slate-500">24% simple / 24 mo</span></div>
        </div>
      </div>

      {/* SBA Loan Above $350K Eligibility (up to $5M) — uses Adjusted EBITDA for DSCR sizing */}
      <SBAEligibilityCard
        ebitda={adjustedEbitda}
        revenue={revenue}
        existingBusinessDebt={existingDebt}
        industry={data.industry || data.naics_code_or_nature}
        timeInBusinessYears={data.time_in_business_years || data.years_in_business}
      />

      {/* Quidity Jumbo RBF Eligibility */}
      <div className={`p-3 rounded-xl border ${qualifiesJumboRBF ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-xs font-bold text-slate-700 uppercase">Quidity Jumbo RBF</h5>
          <Badge className={`text-[10px] ${qualifiesJumboRBF ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`} data-testid="tax-jumbo-rbf-status">
            {qualifiesJumboRBF ? 'Potentially Eligible' : 'Does Not Qualify'}
          </Badge>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs"><span className="text-slate-600">Max Purchase Amount</span><span className="font-bold text-slate-800" data-testid="tax-jumbo-rbf-max">{fmt(maxJumboRBF)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Monthly Payment</span><span className="font-bold text-slate-800">{fmt(monthlyPmtJumboRBF)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Projected DSCR</span><span className={`font-bold ${dscrJumboRBF && dscrJumboRBF >= 1.0 ? 'text-emerald-700' : 'text-red-600'}`}>{dscrJumboRBF ? `${dscrJumboRBF.toFixed(2)}x` : 'N/A'}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-600">Min Revenue (Annual)</span><span className="font-medium text-slate-500">$1,800,000</span></div>
          {revenue > 0 && <div className="flex justify-between text-xs"><span className="text-slate-600">Revenue Cap (50%)</span><span className="font-medium text-slate-500">{fmt(revenue * 0.5)}</span></div>}
          <div className="flex justify-between text-xs"><span className="text-slate-600">Rate / Term</span><span className="font-medium text-slate-500">24% simple / 12 mo</span></div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-800 leading-relaxed" data-testid="tax-disclaimer">
            <strong>Disclaimer:</strong> This analysis is based solely on the financial data extracted from the tax return.
            The borrower must still credit qualify based on FICO score, time in business, industry restrictions,
            and other underwriting criteria. Actual terms may vary. Term Loan estimates use 18% amortized;
            the Quidity Jumbo Term Loan uses 24% simple interest / 24 months and the Quidity Jumbo RBF uses
            24% simple interest / 12 months.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BrokerToolkit;
